import { env } from '@/config/env';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';
import { redirectTo } from '@/contexts/NavigationContext';
import { toast } from '@/components/ui/use-toast';

const api = axios.create({
  baseURL: env.API_BASE_URL,
  headers: {
    'Accept-Language': 'es',
  },
});

const updateTokens = async (accessToken: string, refreshToken: string) => {
  // Every call site here either destroys the session (`updateTokens('', '')` on a
  // forced logout) or installs a brand new one (login/register through
  // `setAuthTokens`), so a support session can never outlive any of them. It has
  // to be dropped explicitly: this writes through `setState` rather than the
  // store's `clearTokens`, so the impersonation cleanup baked into that action
  // does not apply. Without this, a support session that dies takes the admin to
  // /login, and their next login rehydrates `impersonation` from storage — the
  // banner then claims they are acting as someone else over an ordinary session,
  // backed by parked tokens that are long dead.
  //
  // Leaving the support session is NOT one of these paths: `stopImpersonation`
  // restores the parked tokens through the store, never through here.
  useAuthStore.setState({ accessToken, refreshToken, impersonation: null });

  // Initialize fees when tokens are updated (login or refresh)
  if (accessToken && refreshToken) {
    try {
      const { useFeeStore } = await import('../store/useFeeStore');
      const { fetchFixedFeeAmount } = useFeeStore.getState();
      await fetchFixedFeeAmount();
    } catch (error) {
      console.error('Error initializing fees after token update:', error);
    }
  } else {
    // Clear tournament data when tokens are cleared (logout or token revocation)
    try {
      const { useTournamentStore } = await import('../store/useTournamentStore');
      const { clearTournaments } = useTournamentStore.getState();
      clearTournaments();
    } catch (error) {
      console.error('Error clearing tournaments after token clear:', error);
    }
  }
};

const SESSION_HANDLED_KEY = '__getmatchSessionHandled';

/**
 * Stamps a rejection the interceptor has already settled at the session level:
 * the request failed, but the session it belonged to was either restored or
 * deliberately replaced, and the one that is current now is valid.
 *
 * Callers that react to a failed request by logging out (see `useAuth`) must
 * skip that here, or they undo the recovery the interceptor just performed.
 */
const markSessionHandled = (error: unknown) => {
  if (error && typeof error === 'object') {
    (error as Record<string, unknown>)[SESSION_HANDLED_KEY] = true;
  }
  return error;
};

export const isSessionHandledError = (error: unknown): boolean =>
  !!error &&
  typeof error === 'object' &&
  (error as Record<string, unknown>)[SESSION_HANDLED_KEY] === true;

const SESSION_NOTIFIED_KEY = '__getmatchSessionNotified';

/**
 * Stamps a rejection the interceptor has already explained to the user with a
 * toast of its own, so `handleGlobalError` stays quiet instead of replacing the
 * real reason with the caller's screen-level copy.
 *
 * Deliberately narrower than `markSessionHandled`. That one also covers the
 * cross-session replay guard below, which shows nothing of its own: silencing
 * the caller there would turn a failed request into a blank screen with no
 * explanation and no retry cue.
 */
const markSessionNotified = (error: unknown) => {
  if (error && typeof error === 'object') {
    (error as Record<string, unknown>)[SESSION_NOTIFIED_KEY] = true;
  }
  return error;
};

export const isSessionNotifiedError = (error: unknown): boolean =>
  !!error &&
  typeof error === 'object' &&
  (error as Record<string, unknown>)[SESSION_NOTIFIED_KEY] === true;

/**
 * The Authorization a request carries, read case-insensitively.
 *
 * `headers.Authorization` is a raw property read on an `AxiosHeaders`: it only
 * matches callers that spell the header exactly as we do here, and a caller
 * writing `authorization` would silently look header-less to everything below.
 */
const authorizationHeader = (
  request: InternalAxiosRequestConfig | undefined,
): string | null => {
  const authorization = request?.headers?.get('Authorization');
  return typeof authorization === 'string' ? authorization : null;
};

/** The Bearer token a request actually went out with, if it carried one. */
const issuedBearerToken = (
  request: InternalAxiosRequestConfig | undefined,
): string | null => {
  const authorization = authorizationHeader(request);
  return authorization?.startsWith('Bearer ')
    ? authorization.slice('Bearer '.length)
    : null;
};

const refreshAccessToken = async (refreshToken: string) => {
  const response = await axios.post(
    `${env.API_BASE_URL}/token`,
    {},
    {
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
    },
  );
  return response.data;
};

const isPasswordLoginTokenRequest = (config: InternalAxiosRequestConfig) => {
  const url = config.url ?? '';
  const method = config.method?.toLowerCase() ?? '';
  // Axios may set url as relative path, absolute, or without leading slash — match loosely.
  const isManagerPasswordToken =
    url.includes('managers/token') && !url.includes('google');
  return method === 'post' && isManagerPasswordToken;
};

api.interceptors.request.use((config) => {
  // An Authorization set on the request itself wins over the stored token, and
  // the two callers that set one both need it to: password login sends Basic,
  // and the impersonation takeover has to travel with the admin's own parked
  // token while the borrowed one is the active session.
  if (config.headers.has('Authorization')) {
    return config;
  }
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // A borrowed session has no refresh token and expires on its own after 30
    // minutes. Letting it fall through to the refresh branch would clear the
    // tokens and bounce to /login — costing the admin their OWN session over a
    // token that was only ever borrowed. Restore it and send them back instead.
    // This has to stay above the refresh branch: by the time that one is done,
    // the tokens (the parked admin ones included) are already gone.
    //
    // Only a 401 that belonged to the borrowed session ends it, hence the token
    // check: the takeover POST deliberately travels with the admin's OWN parked
    // token while the borrowed one is the active session, and tearing the
    // support session down over that request's 401 would punish the admin for
    // their own token expiring. Same reasoning as the replay guard below —
    // read the token the request actually went out with, not store state at
    // rejection time.
    const { impersonation, accessToken: activeToken } = useAuthStore.getState();
    if (
      error.response?.status === 401 &&
      impersonation &&
      issuedBearerToken(originalRequest) === activeToken
    ) {
      useAuthStore.getState().stopImpersonation();
      redirectTo('/admin/torneos');
      // The spec asks for the admin to land back in the backoffice "con un
      // aviso": without it the screen simply jumps and the only copy they see
      // is whatever generic message the caller passes to handleGlobalError —
      // "no se pudieron cargar los torneos" for an expiry that had nothing to
      // do with loading anything. `markSessionHandled` below is what keeps that
      // one from overwriting this toast.
      toast({
        title: 'La sesión de soporte venció',
        description: 'Volviste a tu cuenta. Entrá de nuevo si necesitás seguir.',
      });
      // Handled, or the recovery is undone one frame later: `useAuth` runs on
      // every protected page and logs out on a failed user fetch — which is
      // exactly the request that fails here after an F5 mid-support. Notified
      // as well, because the toast above is the explanation the user gets.
      return Promise.reject(markSessionNotified(markSessionHandled(error)));
    }

    // 401 only. A 403 is "you are authenticated but you cannot do this":
    // refreshing the token changes nothing, and logging out over it throws the
    // user out of the app for touching a screen that was never theirs. Grouping
    // the two was harmless while the backend answered 500 on a missing
    // permission; ever since CustomExceptionHandler mapped AccessDeniedException
    // to 403, every real 403 logged the user out. The expired-token 401 still
    // comes from JwtAuthenticationFilter.
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isPasswordLoginTokenRequest(originalRequest)
    ) {
      // Refreshing renews the session the request belongs to, and the replay
      // below re-sends the request with whatever token comes back. Both only
      // make sense while that is still the session in play. When tokens change
      // while requests are in flight — a support session ending, a logout, a
      // login as somebody else — the losing request would be replayed against
      // an account that never issued it. Concretely: two requests 401 as the
      // borrowed session dies, the first restores the admin above, and the
      // second would refresh the ADMIN's session and replay a request made as
      // the impersonated user against the admin's account — a write, in
      // takeover mode. Reject instead; replaying with the current token is the
      // same bug by another route.
      const issuedToken = issuedBearerToken(originalRequest);
      if (issuedToken && issuedToken !== useAuthStore.getState().accessToken) {
        return Promise.reject(markSessionHandled(error));
      }

      originalRequest._retry = true;
      const { refreshToken } = useAuthStore.getState();

      if (!refreshToken) {
        await updateTokens('', '');
        redirectTo('/login');
        return Promise.reject(error);
      }

      try {
        const { accessToken, refreshToken: newRefreshToken } =
          await refreshAccessToken(refreshToken);
        await updateTokens(accessToken, newRefreshToken);
        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        await updateTokens('', '');
        redirectTo('/login');
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export const setAuthTokens = async (
  accessToken: string,
  refreshToken: string,
) => {
  await updateTokens(accessToken, refreshToken);
};

export default api;
